/**
 * Auth Types
 *
 * spec 3e-02 W3: Updated for codaic-native auth-service bearer token shape.
 *
 * Breaking changes from identity-service cookie shape:
 *   - id: number → string (UUID)
 *   - role: string → roles: string[] (primary role derived as role = roles[0])
 *   - full_name removed from backend (derived as first_name + ' ' + last_name)
 *   - token delivered in JSON body (not httpOnly cookie)
 *   - token_type: 'cookie' → 'bearer'
 */

export interface User {
  id: string;           // UUID from auth-service (was: number)
  username: string;
  email: string;
  role: UserRole;       // Primary role — derived from roles[0] on login
  roles?: string[];     // Full roles array from /auth/me
  status?: UserStatus;
  full_name?: string;   // Derived: first_name + ' ' + last_name
  first_name?: string;
  last_name?: string;
  firstName?: string;   // Alias kept for legacy UI consumers
  lastName?: string;    // Alias kept for legacy UI consumers
  department?: string;
  tenant_id?: string;
  is_active?: boolean;
  /** ISO 8601 datetime string from backend (e.g., "2025-01-15T10:30:00Z") */
  last_login?: string;
  /** ISO 8601 datetime string from backend */
  created_at?: string;
}

export type UserRole = 'admin' | 'portfolio_manager' | 'analyst' | 'static_data' | 'viewer';

/**
 * User status values.
 * Backend validates these as string literals: 'Active' or 'Inactive'
 */
export type UserStatus = 'Active' | 'Inactive';

/**
 * Login credentials for authentication.
 * Backend validates: username min 3 chars, password min 6 chars.
 */
export interface LoginCredentials {
  username: string;
  password: string;
}

/**
 * Codaic-native auth-service login response.
 * POST /auth/login → {access_token, refresh_token, token_type, expires_in}
 * This is the bearer-token response from auth-service (spec 3e-02).
 */
export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  token_type: 'bearer';
  expires_in: number;
}

/**
 * Codaic-native auth-service user profile response.
 * GET /auth/me → {id, username, email, first_name, last_name, is_active, tenant_id, roles}
 */
export interface MeResponse {
  id: string;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  tenant_id: string;
  roles: string[];
}

/**
 * Password change request payload.
 * Backend validates:
 * - old_password: minimum 6 characters
 * - new_password: minimum 6 characters
 */
export interface ChangePasswordRequest {
  old_password: string;
  new_password: string;
}

/**
 * Role-based permission definitions
 * Maps each role to the route prefixes they can access
 *
 * A-001 to A-005: Fixed RBAC permission gaps for portfolio_manager role
 */
export const ROLE_PERMISSIONS: Record<UserRole, string[]> = {
  admin: ['*'], // Admin can access everything
  portfolio_manager: [
    '/',                   // E2E-UI: Root path (dashboard redirect)
    '/dashboard',
    '/activity',
    '/trading',
    '/trading-reports',
    '/positions',
    '/analytics',
    '/performance',
    '/fund',
    '/allocation',
    '/fof',
    '/operations',
    '/pricing',
    '/post-trade',         // E2E-UI: Settlement, SSI management, fails
    '/accounting',         // E2E-UI: Accounting dashboard, GL, trial balance
    '/data',               // E2E-UI: All data routes (instruments, market, holding, pricing, quality)
    '/settings',           // A-003: Expanded from /settings/profile to full settings access
    '/oms',                // A-001: Added OMS access for order management
    '/optimization',       // A-005: Added optimization access
    '/rebalance',          // Added rebalancing access
    '/fee-management',     // A-004: Added fee management access
    '/risk-management',    // A-002: Added risk management access
    '/risk-data',          // A-002: Added risk data access
    '/compliance',         // Added compliance access
    '/multi-book',         // Added multi-book access (IBOR/ABOR/RBOR)
    '/curves',             // Added curve management access
    '/reference-data',     // Added reference data access
    '/admin/reference-data', // SEC-QY-03: reference-data admin portal access
    '/admin/operations',   // SEC-QY-03: operations admin portal access
    '/admin/market-data',          // spec-3c-05-md W4: market-data admin portal access (data_admin role)
    '/admin/analytics-reporting',  // spec-3c-05-anr W4: analytics-reporting admin portal (data_admin role)
    '/collateral',         // Added collateral management access
    '/help',               // Added help access
    '/documentation',      // Added documentation access
  ],
  analyst: [
    '/dashboard',
    '/positions',
    '/analytics',
    '/performance',
    '/fund',
    '/allocation',
    '/data/market',
    '/data/pricing',
    '/settings',           // Expanded settings access
    '/oms',                // Added OMS read access
    '/optimization',       // Added optimization read access
    '/risk-management',    // Added risk read access
    '/compliance',         // Added compliance read access
    '/help',
    '/documentation',
  ],
  static_data: [
    '/dashboard',
    '/data',
    '/reference-data',
    '/admin/reference-data', // SEC-QY-03: reference-data admin portal access
    '/settings',
    '/help',
    '/documentation',
  ],
  viewer: [
    '/dashboard',
    '/positions',
    '/analytics',
    '/performance',
    '/settings',
    '/help',
    '/documentation',
  ],
};

/**
 * Check if a user role has permission to access a given path
 */
export function hasPermission(role: UserRole | undefined, path: string): boolean {
  if (!role) return false;

  const permissions = ROLE_PERMISSIONS[role];
  if (!permissions) return false;

  // Admin has access to everything
  if (permissions.includes('*')) return true;

  // Check if the path starts with any allowed prefix
  return permissions.some(prefix => path.startsWith(prefix));
}
